import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { cloneElements } from "../../utils/undoSnapshots";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import {
    extractViewBox,
    isValidSvg,
    getSvgDimensions,
} from "../../utils/svgUtils";
import {
    parseSvgElements,
    generateGridPattern,
    getParsedElementCount,
    type ParsedSvgElement,
} from "../../utils/svgUtils";

interface UploadedSvg {
    name: string;
    content: string;
}

interface GridEditorState {
    objectSvg: UploadedSvg | null;
    error: string | null;
    columns: number;
    rows: number;
    cellWidth: number;
    cellHeight: number;
    cellSpacingX: number;
    cellSpacingY: number;
    density: number;
    fillPattern:
        | "even"
        | "linear-gradient"
        | "radial-gradient"
        | "reverse-radial-gradient"
        | "perlin-noise";
    startPosition: string;
    endPosition: string;
    noise: number;
    dithering: number;
    elements: ParsedSvgElement[];
    selectedElementIds: Set<string>;
    copiedElements: ParsedSvgElement[];
    draggingElementId: string | null;
    dragOffset: { x: number; y: number } | null;
    viewBoxAttr: string;
    gridSvgContent: string | null;
    setObjectSvg: (svg: UploadedSvg | null) => void;
    setError: (e: string | null) => void;
    setColumns: (c: number) => void;
    setRows: (r: number) => void;
    setCellWidth: (w: number) => void;
    setCellHeight: (h: number) => void;
    setCellSpacingX: (s: number) => void;
    setCellSpacingY: (s: number) => void;
    setDensity: (d: number) => void;
    setFillPattern: (
        p:
            | "even"
            | "linear-gradient"
            | "radial-gradient"
            | "reverse-radial-gradient"
            | "perlin-noise"
    ) => void;
    setStartPosition: (p: string) => void;
    setEndPosition: (p: string) => void;
    setNoise: (n: number) => void;
    setDithering: (d: number) => void;
    svgInputRef: React.RefObject<HTMLInputElement>;
    handleFileLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
    selectElement: (id: string, multiSelect?: boolean) => void;
    deselectElement: (id: string) => void;
    clearSelection: () => void;
    selectAllElements: () => void;
    setSelectedElements: (ids: string[]) => void;
    startDrag: (id: string, offsetX: number, offsetY: number) => void;
    updateDrag: (x: number, y: number) => void;
    endDrag: () => void;
    copyElements: () => void;
    pasteElements: () => void;
    updateElementPosition: (id: string, x: number, y: number) => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const GridEditorContext = createContext<GridEditorState | null>(null);

export function useGridEditor() {
    const context = useContext(GridEditorContext);
    if (!context) {
        throw new Error("useGridEditor must be used within GridEditorProvider");
    }
    return context;
}

interface GridEditorProviderProps {
    children: React.ReactNode;
}

const DEFAULT_GRID_EDITOR_PRESET: Record<string, unknown> = {
    columns: 30,
    rows: 30,
    cellWidth: 100,
    cellHeight: 100,
    cellSpacingX: 0,
    cellSpacingY: 0,
    density: 100,
    fillPattern: "even",
    startPosition: "top-left",
    endPosition: "bottom-right",
    noise: 0,
    dithering: 0,
};

export function GridEditorProvider({ children }: GridEditorProviderProps) {
    const [objectSvg, setObjectSvg] = useState<UploadedSvg | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [columns, setColumns] = useState(30);
    const [rows, setRows] = useState(30);
    const [cellWidth, setCellWidth] = useState(100);
    const [cellHeight, setCellHeight] = useState(100);
    const [cellSpacingX, setCellSpacingX] = useState(0);
    const [cellSpacingY, setCellSpacingY] = useState(0);
    const [density, setDensity] = useState(100);
    const [fillPattern, setFillPattern] = useState<
        | "even"
        | "linear-gradient"
        | "radial-gradient"
        | "reverse-radial-gradient"
        | "perlin-noise"
    >("even");
    const [startPosition, setStartPosition] = useState("top-left");
    const [endPosition, setEndPosition] = useState("bottom-right");
    const [noise, setNoise] = useState(0);
    const [dithering, setDithering] = useState(0);
    const [elements, setElements] = useState<ParsedSvgElement[]>([]);
    const [selectedElementIds, setSelectedElementIds] = useState<Set<string>>(
        new Set()
    );
    const [copiedElements, setCopiedElements] = useState<ParsedSvgElement[]>(
        []
    );
    const [draggingElementId, setDraggingElementId] = useState<string | null>(
        null
    );
    const [dragOffset, setDragOffset] = useState<{
        x: number;
        y: number;
    } | null>(null);

    const svgInputRef = useRef<HTMLInputElement | null>(null);
    const elementsRef = useRef(elements);
    elementsRef.current = elements;
    const history = useUndoRedo<ParsedSvgElement[]>({ clone: cloneElements });

    const pushElementsSnapshot = useCallback(() => {
        history.push(elementsRef.current);
    }, [history]);

    const undo = useCallback(() => {
        history.undo(elementsRef.current, setElements);
    }, [history]);

    const redo = useCallback(() => {
        history.redo(elementsRef.current, setElements);
    }, [history]);

    const ingestSvgFromText = useCallback((name: string, text: string) => {
        setError(null);

        if (!text) {
            setError("Unable to read SVG file.");
            return;
        }

        if (!isValidSvg(text)) {
            setError("Selected file does not appear to be valid SVG.");
            return;
        }

        setObjectSvg({ name, content: text });

        const elementCount = getParsedElementCount(text);
        if (elementCount > 500) {
            setError(
                `SVG contains ${elementCount} elements. Only the first 500 will be editable for performance. Consider simplifying the SVG or grouping elements.`,
            );
        }

        const parsedElements = parseSvgElements(text);
        history.clear();
        setElements(parsedElements);
        setSelectedElementIds(new Set());
    }, [history]);

    const handleFileLoad = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const file = event.target.files?.[0];
        if (!file) return;

        setError(null);

        if (
            !file.type.includes("svg") &&
            !file.name.toLowerCase().endsWith(".svg")
        ) {
            setError("Please choose an SVG file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";
            ingestSvgFromText(file.name, text);
        };

        reader.onerror = () => {
            setError("Error reading file.");
        };

        reader.readAsText(file);
    };

    const viewBoxAttr = useMemo(() => {
        if (!objectSvg) return "0 0 200 200";
        return extractViewBox(objectSvg.content)?.viewBox ?? "0 0 200 200";
    }, [objectSvg]);

    const selectElement = (id: string, multiSelect = false) => {
        setSelectedElementIds((prev) => {
            const newSet = new Set(prev);
            if (multiSelect) {
                if (newSet.has(id)) {
                    newSet.delete(id);
                } else {
                    newSet.add(id);
                }
            } else {
                newSet.clear();
                newSet.add(id);
            }
            return newSet;
        });
    };

    const deselectElement = (id: string) => {
        setSelectedElementIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    const clearSelection = () => {
        setSelectedElementIds(new Set());
    };

    const selectAllElements = () => {
        setSelectedElementIds(new Set(elements.map((el) => el.id)));
    };

    const setSelectedElements = (ids: string[]) => {
        setSelectedElementIds(new Set(ids));
    };

    const startDrag = (id: string, offsetX: number, offsetY: number) => {
        pushElementsSnapshot();
        setDraggingElementId(id);
        setDragOffset({ x: offsetX, y: offsetY });
    };

    const updateDrag = (x: number, y: number) => {
        if (!draggingElementId || !dragOffset) return;

        const element = elements.find((e) => e.id === draggingElementId);
        if (!element) return;

        const newX = x - dragOffset.x;
        const newY = y - dragOffset.y;

        // Get the element's original position before transform
        let originalX = 0;
        let originalY = 0;
        if (element.transform) {
            const translateMatch = element.transform.match(
                /translate\(([^,]+),\s*([^)]+)\)/
            );
            if (translateMatch) {
                originalX = parseFloat(translateMatch[1]) || 0;
                originalY = parseFloat(translateMatch[2]) || 0;
            }
        }

        // Calculate the delta (how much we moved)
        const deltaX = newX - originalX;
        const deltaY = newY - originalY;

        // Move the dragged element
        updateElementPosition(draggingElementId, newX, newY);

        // If there are other selected elements, move them by the same delta
        const otherSelectedElements = elements.filter(
            (e) => e.id !== draggingElementId && selectedElementIds.has(e.id)
        );

        for (const otherElement of otherSelectedElements) {
            let otherX = 0;
            let otherY = 0;
            if (otherElement.transform) {
                const translateMatch = otherElement.transform.match(
                    /translate\(([^,]+),\s*([^)]+)\)/
                );
                if (translateMatch) {
                    otherX = parseFloat(translateMatch[1]) || 0;
                    otherY = parseFloat(translateMatch[2]) || 0;
                }
            }

            updateElementPosition(
                otherElement.id,
                otherX + deltaX,
                otherY + deltaY
            );
        }
    };

    const endDrag = () => {
        setDraggingElementId(null);
        setDragOffset(null);
    };

    const updateElementPosition = (id: string, x: number, y: number) => {
        setElements((prev) =>
            prev.map((el) => {
                if (el.id === id) {
                    const newTransform = `translate(${x}, ${y})`;
                    return {
                        ...el,
                        transform: newTransform,
                    };
                }
                return el;
            })
        );
    };

    const copyElements = () => {
        const selectedElements = elements.filter((el) =>
            selectedElementIds.has(el.id)
        );
        if (selectedElements.length > 0) {
            setCopiedElements(selectedElements.map((el) => ({ ...el })));
        }
    };

    const pasteElements = () => {
        if (copiedElements.length === 0) return;
        pushElementsSnapshot();

        const newElements = copiedElements.map((el) => {
            const newId = `${el.id}-copy-${Date.now()}-${Math.random()}`;
            return {
                ...el,
                id: newId,
            };
        });

        setElements((prev) => [...prev, ...newElements]);
        setSelectedElementIds(new Set(newElements.map((el) => el.id)));
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            columns, rows, cellWidth, cellHeight, cellSpacingX, cellSpacingY,
            density, fillPattern, startPosition, endPosition, noise, dithering,
        }),
        [
            columns, rows, cellWidth, cellHeight, cellSpacingX, cellSpacingY,
            density, fillPattern, startPosition, endPosition, noise, dithering,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.columns != null) setColumns(values.columns as number);
        if (values.rows != null) setRows(values.rows as number);
        if (values.cellWidth != null) setCellWidth(values.cellWidth as number);
        if (values.cellHeight != null) setCellHeight(values.cellHeight as number);
        if (values.cellSpacingX != null) setCellSpacingX(values.cellSpacingX as number);
        if (values.cellSpacingY != null) setCellSpacingY(values.cellSpacingY as number);
        if (values.density != null) setDensity(values.density as number);
        if (values.fillPattern != null) setFillPattern(values.fillPattern as "even" | "linear-gradient" | "radial-gradient" | "reverse-radial-gradient" | "perlin-noise");
        if (values.startPosition != null) setStartPosition(values.startPosition as string);
        if (values.endPosition != null) setEndPosition(values.endPosition as string);
        if (values.noise != null) setNoise(values.noise as number);
        if (values.dithering != null) setDithering(values.dithering as number);
    }, []);

    const { resetParameters: resetStoredParameters } = useToolParamPersistence({
        toolId: "grid-editor",
        defaultPreset: DEFAULT_GRID_EDITOR_PRESET,
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        history.clear();
        resetStoredParameters();
    }, [history, resetStoredParameters]);

    useSvgToolHandoffReceiver(
        "grid-editor",
        {
            onPrimary: (svg) => ingestSvgFromText(svg.name, svg.content),
        },
        { setError },
    );

    // Generate grid SVG content
    const gridSvgContent = useMemo(() => {
        if (!objectSvg || elements.length === 0) return null;

        // Rebuild object SVG with current element positions
        const objectSvgContent = generateObjectSvgFromElements(
            objectSvg.content,
            elements
        );

        return generateGridPattern(
            objectSvgContent,
            columns,
            rows,
            cellWidth,
            cellHeight,
            cellSpacingX,
            cellSpacingY,
            density,
            fillPattern,
            startPosition,
            endPosition,
            noise,
            dithering
        );
    }, [
        objectSvg,
        elements,
        columns,
        rows,
        cellWidth,
        cellHeight,
        cellSpacingX,
        cellSpacingY,
        density,
        fillPattern,
        startPosition,
        endPosition,
        noise,
        dithering,
    ]);

    const value: GridEditorState = {
        objectSvg,
        error,
        columns,
        rows,
        cellWidth,
        cellHeight,
        cellSpacingX,
        cellSpacingY,
        density,
        fillPattern,
        startPosition,
        endPosition,
        noise,
        dithering,
        elements,
        selectedElementIds,
        copiedElements,
        draggingElementId,
        dragOffset,
        viewBoxAttr,
        gridSvgContent,
        setObjectSvg,
        setError,
        setColumns,
        setRows,
        setCellWidth,
        setCellHeight,
        setCellSpacingX,
        setCellSpacingY,
        setDensity,
        setFillPattern,
        setStartPosition,
        setEndPosition,
        setNoise,
        setDithering,
        svgInputRef,
        handleFileLoad,
        selectElement,
        deselectElement,
        clearSelection,
        selectAllElements,
        setSelectedElements,
        startDrag,
        updateDrag,
        endDrag,
        copyElements,
        pasteElements,
        updateElementPosition,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo: history.canUndo,
        canRedo: history.canRedo,
    };

    return (
        <GridEditorContext.Provider value={value}>
            {children}
        </GridEditorContext.Provider>
    );
}

// Helper function to rebuild SVG from elements
function generateObjectSvgFromElements(
    originalSvg: string,
    elements: ParsedSvgElement[]
): string {
    // Separate original elements (that exist in the SVG) from pasted elements (new ones)
    // Pasted elements have IDs containing "-copy-"
    const originalElements = elements.filter((el) => !el.id.includes("-copy-"));
    const pastedElements = elements.filter((el) => el.id.includes("-copy-"));

    // First, replace original elements in place
    // Sort elements by startIndex descending to replace from end to start
    const sortedOriginalElements = [...originalElements].sort(
        (a, b) => b.startIndex - a.startIndex
    );

    let result = originalSvg;
    for (const element of sortedOriginalElements) {
        // Build new element string with transform
        const attrs = { ...element.attributes };
        if (element.transform) {
            attrs.transform = element.transform;
        }

        const attrsString = Object.entries(attrs)
            .filter(([_, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${key}="${value}"`)
            .join(" ");

        const newElement = `<${element.type} ${attrsString} />`;

        result =
            result.slice(0, element.startIndex) +
            newElement +
            result.slice(element.endIndex);
    }

    // Then, append pasted elements to the SVG content
    // Find the closing </svg> tag and insert pasted elements before it
    if (pastedElements.length > 0) {
        const closingTagIndex = result.lastIndexOf("</svg>");
        if (closingTagIndex !== -1) {
            // Build pasted elements
            const pastedElementsString = pastedElements
                .map((element) => {
                    const attrs = { ...element.attributes };
                    if (element.transform) {
                        attrs.transform = element.transform;
                    }

                    const attrsString = Object.entries(attrs)
                        .filter(
                            ([_, value]) =>
                                value !== undefined && value !== null
                        )
                        .map(([key, value]) => `${key}="${value}"`)
                        .join(" ");

                    return `  <${element.type} ${attrsString} />`;
                })
                .join("\n");

            // Insert pasted elements before closing tag
            result =
                result.slice(0, closingTagIndex) +
                "\n" +
                pastedElementsString +
                "\n" +
                result.slice(closingTagIndex);
        }
    }

    return result;
}
